import * as React from "react";
import { AlarmClockCheck } from "lucide-react";
import { clsx } from "clsx";
import { Button } from "@/components/ui/button";
import { FolderItem, Item } from "@/types/fse";
import { useNavigate } from "react-router";
import { useCreateChronologiesMutation } from "@/api/chronology";
import TopBarProgress from "react-topbar-progress-indicator";
import { toast } from "react-toastify";
import * as analytics from "@/services/analytics";
import { ChronologyOnFolderWarningModal } from "@/views/chronology/modals/chronologyOnFolderWarningModal";
import { useToggle } from "react-use";
import { ChronologyFolderRegenerateModal } from "@/views/chronology/modals/chronologyFolderRegenerateModal";
import { FastApiErrorResponse } from "@/api/base";

interface Props {
  item: Item;
  isDisabled: boolean;
  parentFolder: FolderItem | null;
}

export const DriveRowButtonsChronology: React.FC<Props> = ({ item, isDisabled, parentFolder }) => {
  const navigate = useNavigate();
  const [showWarning, toggleShowWarning] = useToggle(false);
  const [showRegenerateModal, toggleShowRegenerateModal] = useToggle(false);

  const [handleCreateChronologies, { isLoading }] = useCreateChronologiesMutation();
  const hasChronology = (item.transactions?.chronology?.progress || 0) >= 1;

  const inProgress = typeof item.transactions?.chronology?.progress !== "undefined" && !hasChronology;

  const handleGenerate = async () => {
    try {
      await handleCreateChronologies(item.id).unwrap();
      analytics.chronologyCreate(item.id);
    } catch (error) {
      if ((error as FastApiErrorResponse).data.err_type === "NoDocumentsToProcessError") {
        toast.error("Can't create chronology for this folder, folder is empty");
      } else {
        toast.error("Error while creating chronology");
      }
    }
  };
  const handleAccept = async () => {
    if (hasChronology) {
      const url =
        parentFolder?.transactions?.chronology?.progress === 1
          ? `/chronology/${parentFolder.id}/${item.id}`
          : `/chronology/${item.id}`;

      navigate(url);
    } else {
      handleGenerate();
    }
  };

  const handleClick = () => {
    if (item.type === "file") {
      handleAccept();
    } else {
      if (!hasChronology) {
        toggleShowWarning(true);
      } else {
        toggleShowRegenerateModal(true);
      }
    }
  };

  return (
    <>
      <ChronologyFolderRegenerateModal
        item={item}
        onAccept={handleGenerate}
        setIsOpen={toggleShowRegenerateModal}
        isOpen={showRegenerateModal}
      />
      <ChronologyOnFolderWarningModal onAccept={handleAccept} setIsOpen={toggleShowWarning} isOpen={showWarning} />
      <Button
        variant="secondary"
        size="sm"
        className={clsx("w-full text-xs sm:w-auto", (isDisabled || inProgress) && !hasChronology && "opacity-50")}
        onClick={handleClick}
        disabled={(inProgress || isLoading || isDisabled) && !hasChronology}
      >
        <AlarmClockCheck className="mr-1 h-4 w-4" />
        Chronology
      </Button>
      {isLoading && <TopBarProgress />}
    </>
  );
};
